<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import GoogleCalendarAPI from 'dashboard/api/googleCalendar';

const props = defineProps({
  funnelId: { type: [Number, String], required: true },
  funnelName: { type: String, default: '' },
});

const status = ref(null);
const loading = ref(false);
const connecting = ref(false);

const isConnected = computed(() => status.value?.connected === true);
// Saudavel = conectado E autorizacao ativa. Qualquer outro status
// (error/revoked/expired) significa que a integracao NAO esta enviando
// agendamentos pro Google — precisa reconectar.
const isHealthy = computed(
  () =>
    isConnected.value &&
    (status.value?.usable === true || status.value?.status === 'active')
);
const hasProblem = computed(() => isConnected.value && !isHealthy.value);
const problemReason = computed(() => {
  const s = status.value?.status;
  const err = (status.value?.last_error || '').toLowerCase();
  if (err.includes('scope') || err.includes('permiss')) {
    return 'Faltou permitir o acesso a Agenda na autorizacao do Google. Reconecte e, na tela do Google, deixe MARCADA a permissao "Ver, editar e gerenciar eventos da Agenda".';
  }
  if (s === 'revoked')
    return 'O acesso foi revogado no Google. Reconecte para religar a integracao.';
  if (s === 'expired')
    return 'A autorizacao do Google expirou. Reconecte para renovar o acesso.';
  return 'A conexao com o Google nao esta ativa (provavel falta de permissao de Agenda). Reconecte e permita o acesso a Agenda.';
});

const fetchStatus = async () => {
  if (!props.funnelId) return;
  try {
    loading.value = true;
    const { data } = await GoogleCalendarAPI.status(props.funnelId);
    status.value = data;
  } catch (e) {
    status.value = { connected: false };
  } finally {
    loading.value = false;
  }
};

const handleMessage = event => {
  if (event.data?.type === 'google_calendar_connected') {
    connecting.value = false;
    if (event.data.success) {
      useAlert('Google Calendar conectado ao funil com sucesso.');
      fetchStatus();
    } else {
      useAlert(`Erro ao conectar: ${event.data.error || 'desconhecido'}`);
    }
  }
};

const connect = async () => {
  try {
    connecting.value = true;
    const { data } = await GoogleCalendarAPI.connect(props.funnelId);
    const popup = window.open(
      data.authorize_url,
      'google_calendar_oauth',
      'width=500,height=700,menubar=no,toolbar=no'
    );
    if (!popup) {
      connecting.value = false;
      useAlert('Pop-up bloqueado. Permita pop-ups e tente novamente.');
    }
  } catch (e) {
    connecting.value = false;
    const msg =
      e?.response?.data?.message ||
      'Falha ao iniciar OAuth. Verifique se as credenciais Google estao configuradas e tente novamente.';
    useAlert(msg);
  }
};

const updatingStage = ref(false);

const stageOptions = computed(() => {
  const stages = status.value?.funnel_stages || {};
  return Object.keys(stages).map(key => ({
    key,
    name: stages[key]?.name || key,
    color: stages[key]?.color,
  }));
});

const selectedStage = ref('');

watch(
  status,
  s => {
    if (s?.default_stage_for_imports)
      selectedStage.value = s.default_stage_for_imports;
  },
  { immediate: true }
);

const saveDefaultStage = async () => {
  if (updatingStage.value) return;
  updatingStage.value = true;
  try {
    const { data } = await GoogleCalendarAPI.update(props.funnelId, {
      default_stage_for_imports: selectedStage.value || '',
    });
    status.value = data;
    useAlert(
      selectedStage.value
        ? `Eventos importados serão criados como cards do kanban na etapa "${stageOptions.value.find(s => s.key === selectedStage.value)?.name || selectedStage.value}".`
        : 'Auto-criação de cards desativada — eventos só aparecem na Agenda.'
    );
  } catch (e) {
    useAlert('Falha ao salvar etapa default.');
  } finally {
    updatingStage.value = false;
  }
};

const toggleImportExternal = async () => {
  try {
    const { data } = await GoogleCalendarAPI.update(props.funnelId, {
      import_external_events: !status.value.import_external_events,
    });
    status.value = data;
    useAlert(
      data.import_external_events
        ? 'Importação de eventos externos ativada.'
        : 'Importação de eventos externos desativada.'
    );
  } catch (e) {
    useAlert('Falha ao atualizar configuração.');
  }
};

const DURATION_PRESETS = [15, 30, 45, 60, 90, 120, 180];
const selectedDuration = ref(30);
const updatingDuration = ref(false);

const updateDefaultDuration = async () => {
  if (selectedDuration.value === status.value.default_duration_min) return;
  updatingDuration.value = true;
  try {
    const { data } = await GoogleCalendarAPI.update(props.funnelId, {
      default_duration_min: selectedDuration.value,
    });
    status.value = data;
    useAlert(`Duracao padrao atualizada para ${selectedDuration.value} min.`);
  } catch (e) {
    useAlert('Falha ao salvar duracao padrao.');
  } finally {
    updatingDuration.value = false;
  }
};

// Sync agora — botao de diagnostico pra forcar pull imediato sem esperar cron
const syncingNow = ref(false);
const triggerSyncNow = async () => {
  if (syncingNow.value) return;
  syncingNow.value = true;
  try {
    const { data } = await GoogleCalendarAPI.syncNow(props.funnelId);
    if (data.ok) {
      useAlert(
        `Sync OK em ${data.elapsed_ms}ms. Ultimo sync: ${new Date(data.last_synced_at).toLocaleTimeString()}`
      );
      await fetchStatus();
    } else {
      useAlert(
        `Sync nao OK. Status: ${data.status}. Erro: ${data.last_error || 'desconhecido'}`
      );
    }
  } catch (e) {
    const msg =
      e?.response?.data?.error || e?.message || 'Erro ao executar sync';
    useAlert(`Falha no sync: ${msg}`);
  } finally {
    syncingNow.value = false;
  }
};

// Janela de sincronizacao (lookback/lookahead em dias). Limita o backfill
// inicial e impede recurring events explodirem em milhares de instancias.
const selectedLookback = ref(7);
const selectedLookahead = ref(90);
const updatingWindow = ref(false);

const updateSyncWindow = async () => {
  if (
    selectedLookback.value === status.value.sync_lookback_days &&
    selectedLookahead.value === status.value.sync_lookahead_days
  )
    return;
  updatingWindow.value = true;
  try {
    const { data } = await GoogleCalendarAPI.update(props.funnelId, {
      sync_lookback_days: selectedLookback.value,
      sync_lookahead_days: selectedLookahead.value,
    });
    status.value = data;
    useAlert(
      `Janela de sincronizacao atualizada (${selectedLookback.value}d para tras / ${selectedLookahead.value}d a frente).`
    );
  } catch (e) {
    useAlert('Falha ao salvar janela de sincronizacao.');
  } finally {
    updatingWindow.value = false;
  }
};

const disconnect = async () => {
  if (
    !window.confirm(
      `Desconectar Google Calendar do funil "${props.funnelName}"? Eventos já criados não serão removidos do calendário.`
    )
  )
    return;
  try {
    await GoogleCalendarAPI.disconnect(props.funnelId);
    status.value = { connected: false };
    useAlert('Google Calendar desconectado.');
  } catch (e) {
    useAlert('Falha ao desconectar.');
  }
};

const lastSyncedHumanReadable = computed(() => {
  if (!status.value?.last_synced_at) return 'nunca';
  const diff = Math.round(
    (Date.now() - new Date(status.value.last_synced_at).getTime()) / 60000
  );
  if (diff < 1) return 'agora mesmo';
  if (diff < 60) return `${diff} min atrás`;
  const hours = Math.round(diff / 60);
  if (hours < 24) return `${hours}h atrás`;
  return `${Math.round(hours / 24)}d atrás`;
});

watch(
  () => status.value?.sync_lookback_days,
  val => {
    if (val != null) selectedLookback.value = val;
  },
  { immediate: true }
);
watch(
  () => status.value?.sync_lookahead_days,
  val => {
    if (val != null) selectedLookahead.value = val;
  },
  { immediate: true }
);
watch(
  () => status.value?.default_duration_min,
  val => {
    if (val) selectedDuration.value = val;
  },
  { immediate: true }
);
watch(
  () => props.funnelId,
  () => fetchStatus(),
  { immediate: false }
);

onMounted(() => {
  fetchStatus();
  window.addEventListener('message', handleMessage);
});

onBeforeUnmount(() => {
  window.removeEventListener('message', handleMessage);
});
</script>

<template>
  <div class="funnel-google-calendar-section space-y-3">
    <div v-if="loading" class="text-sm text-n-slate-11">Carregando...</div>

    <div v-else-if="!isConnected">
      <p class="text-sm text-n-slate-11 mb-3">
        Conecte uma agenda Google a este funil. Agendamentos criados aqui serão
        sincronizados automaticamente com o calendário escolhido. Útil para
        multi-profissionais — cada funil pode ter sua própria agenda.
      </p>
      <!-- Botao oficial Google estilo "Sign in with Google" -->
      <button
        type="button"
        class="google-signin-btn"
        :disabled="connecting"
        @click="connect"
      >
        <svg
          class="google-signin-icon"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 48 48"
        >
          <path
            fill="#EA4335"
            d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
          />
          <path
            fill="#4285F4"
            d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
          />
          <path
            fill="#FBBC05"
            d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
          />
          <path
            fill="#34A853"
            d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
          />
          <path fill="none" d="M0 0h48v48H0z" />
        </svg>
        <span v-if="connecting">Conectando...</span>
        <span v-else>Entrar com Google</span>
      </button>
    </div>

    <div v-else>
      <!-- Banner de PROBLEMA: conexao existe mas nao esta ativa. Domina o topo
           pra deixar claro que NAO esta tudo certo e os agendamentos NAO estao
           indo pro Google. -->
      <div
        v-if="hasProblem"
        class="mb-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-xl"
      >
        <p class="text-sm font-semibold text-red-800 dark:text-red-200">
          ⚠️ Conexão com problema — os agendamentos NÃO estão sendo enviados
          para o Google Agenda
        </p>
        <p class="text-sm text-red-700 dark:text-red-300 mt-1">
          {{ problemReason }}
        </p>
        <p
          v-if="status.google_email"
          class="text-xs text-red-500 dark:text-red-400 mt-1"
        >
          Conta: {{ status.google_email }}
        </p>
        <p
          v-if="status.last_error"
          class="text-xs text-red-400 dark:text-red-500 mt-1"
        >
          Detalhe técnico: {{ status.last_error }}
        </p>
        <button
          type="button"
          class="mt-3 px-4 py-2 text-sm font-semibold bg-red-600 hover:bg-red-700 text-white rounded-md disabled:opacity-50"
          :disabled="connecting"
          @click="connect"
        >
          {{
            connecting ? 'Abrindo...' : 'Reconectar e permitir acesso à Agenda'
          }}
        </button>
      </div>

      <div class="flex items-start justify-between">
        <div>
          <p class="text-sm font-medium text-n-slate-12">
            <template v-if="isHealthy">
              ✅ Conectado:
              <strong>{{ status.google_email }}</strong>
            </template>
            <template v-else>
              ⚠️ <strong>{{ status.google_email }}</strong> — conexão inativa
            </template>
          </p>
          <p v-if="isHealthy" class="text-xs text-n-slate-11 mt-1">
            Última sincronização: {{ lastSyncedHumanReadable }}
            <span
              v-if="status.watch_active"
              class="ml-2 inline-block px-2 py-0.5 bg-green-100 text-green-800 rounded"
              >real-time</span>
            <span
              v-else
              class="ml-2 inline-block px-2 py-0.5 bg-amber-100 text-amber-800 rounded"
              >cron 5min</span>
          </p>
          <p v-if="status.authorized_by" class="text-xs text-n-slate-10 mt-1">
            Autorizado por: {{ status.authorized_by }}
          </p>
        </div>
        <button
          type="button"
          class="text-sm text-red-600 hover:underline"
          @click="disconnect"
        >
          Desconectar
        </button>
      </div>

      <label
        class="flex items-center gap-2 mt-3 text-sm text-n-slate-12 cursor-pointer"
      >
        <input
          type="checkbox"
          :checked="status.import_external_events"
          @change="toggleImportExternal"
        />
        Importar pro sistema eventos criados externamente neste calendário
      </label>

      <div class="mt-4 p-3 rounded-xl border border-n-weak bg-n-alpha-1">
        <div class="flex items-center gap-2 mb-2">
          <span class="text-sm font-medium text-n-slate-12">Duracao padrao dos compromissos</span>
          <span
            class="text-[10px] uppercase font-semibold tracking-wide bg-n-solid-2 text-n-slate-12 px-2 py-0.5 rounded-full"
            >novo</span>
        </div>
        <p class="text-xs text-n-slate-11 mb-2">
          Tempo padrao usado pra calcular o termino dos eventos no Google
          Calendar quando voce cria um compromisso sem definir duracao
          especifica. Pode ser ajustado caso a caso ao criar.
        </p>
        <div class="flex items-center gap-2">
          <select
            v-model.number="selectedDuration"
            class="flex-1 px-3 py-2 text-sm bg-n-solid-1 border border-n-weak rounded-md focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
          >
            <option v-for="d in DURATION_PRESETS" :key="d" :value="d">
              {{ d }} min
            </option>
          </select>
          <button
            type="button"
            class="px-4 py-2 text-sm font-medium bg-woot-500 hover:bg-woot-600 text-white rounded-md disabled:opacity-50"
            :disabled="
              updatingDuration ||
              selectedDuration === status.default_duration_min
            "
            @click="updateDefaultDuration"
          >
            {{ updatingDuration ? 'Salvando...' : 'Salvar' }}
          </button>
        </div>
      </div>

      <div class="mt-4 p-3 rounded-xl border border-n-weak bg-n-alpha-1">
        <div class="flex items-center gap-2 mb-2">
          <span class="text-sm font-medium text-n-slate-12">Janela de sincronizacao</span>
          <span
            class="text-[10px] uppercase font-semibold tracking-wide bg-n-solid-2 text-n-slate-12 px-2 py-0.5 rounded-full"
            >novo</span>
        </div>
        <p class="text-xs text-n-slate-11 mb-2">
          Quantos dias para tras (eventos passados) e para frente (eventos
          futuros) o sistema deve buscar/sincronizar com o Google Calendar.
          Janela menor = sync mais rapido. Janela maior = mais historico, mas
          pode importar muitos eventos recorrentes.
        </p>
        <div class="grid grid-cols-2 gap-2">
          <label class="text-xs text-n-slate-11">
            <span class="block mb-1">Para tras (dias)</span>
            <input
              v-model.number="selectedLookback"
              type="number"
              min="0"
              max="365"
              class="w-full px-3 py-2 text-sm bg-n-solid-1 border border-n-weak rounded-md focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
            />
          </label>
          <label class="text-xs text-n-slate-11">
            <span class="block mb-1">Para frente (dias)</span>
            <input
              v-model.number="selectedLookahead"
              type="number"
              min="1"
              max="730"
              class="w-full px-3 py-2 text-sm bg-n-solid-1 border border-n-weak rounded-md focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
            />
          </label>
        </div>
        <div class="mt-2 flex justify-end">
          <button
            type="button"
            class="px-4 py-2 text-sm font-medium bg-woot-500 hover:bg-woot-600 text-white rounded-md disabled:opacity-50"
            :disabled="
              updatingWindow ||
              (selectedLookback === status.sync_lookback_days &&
                selectedLookahead === status.sync_lookahead_days)
            "
            @click="updateSyncWindow"
          >
            {{ updatingWindow ? 'Salvando...' : 'Salvar janela' }}
          </button>
        </div>
        <div class="mt-3 pt-3 border-t border-n-weak">
          <button
            type="button"
            class="w-full px-4 py-2 text-sm font-medium border border-n-weak text-n-slate-12 hover:bg-n-alpha-2 rounded-md disabled:opacity-50"
            :disabled="syncingNow"
            @click="triggerSyncNow"
          >
            {{ syncingNow ? 'Sincronizando...' : 'Forcar sync agora (debug)' }}
          </button>
          <p class="text-[11px] text-n-slate-11 mt-1">
            Roda pull imediato sem esperar o cron de 5min. Util pra testar
            mudancas feitas no Google Calendar.
          </p>
        </div>
      </div>

      <div
        v-if="status.import_external_events"
        class="mt-4 p-3 rounded-xl border border-n-weak bg-n-alpha-1"
      >
        <div class="flex items-center gap-2 mb-2">
          <span class="text-sm font-medium text-n-slate-12">Etapa do kanban para eventos importados</span>
          <span
            class="text-[10px] uppercase font-semibold tracking-wide bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 px-2 py-0.5 rounded-full"
            >obrigatório</span>
        </div>
        <p class="text-xs text-n-slate-11 mb-2">
          Cada evento criado no Google Calendar virará automaticamente um card
          do kanban nesta etapa, com título, descrição e horário do evento.
        </p>
        <div class="flex items-center gap-2">
          <select
            v-model="selectedStage"
            class="flex-1 px-3 py-2 text-sm bg-n-solid-1 border border-n-weak rounded-md focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
          >
            <option value="" disabled>Selecione uma etapa...</option>
            <option v-for="opt in stageOptions" :key="opt.key" :value="opt.key">
              {{ opt.name }}
            </option>
          </select>
          <button
            type="button"
            class="px-4 py-2 text-sm font-medium bg-woot-500 hover:bg-woot-600 text-white rounded-md disabled:opacity-50"
            :disabled="
              updatingStage ||
              !selectedStage ||
              selectedStage === status.default_stage_for_imports
            "
            @click="saveDefaultStage"
          >
            {{ updatingStage ? 'Salvando...' : 'Salvar' }}
          </button>
        </div>
        <div
          v-if="status.imports_to_kanban"
          class="mt-2 text-xs text-emerald-700 dark:text-emerald-300"
        >
          ✅ Auto-criação ativa: eventos importados viram cards na etapa "{{
            stageOptions.find(s => s.key === status.default_stage_for_imports)
              ?.name
          }}"
        </div>
        <div v-else class="mt-2 text-xs text-amber-700 dark:text-amber-300">
          ⚠️ Sem etapa selecionada — eventos aparecem na Agenda mas não viram
          cards do kanban.
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.google-signin-btn {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  background-color: #fff;
  color: #1f1f1f;
  border: 1px solid #dadce0;
  border-radius: 4px;
  font-family:
    'Roboto',
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition:
    background-color 120ms ease,
    box-shadow 120ms ease,
    border-color 120ms ease;
  min-height: 40px;
}
.google-signin-btn:hover:not(:disabled) {
  background-color: #f8f9fa;
  border-color: #d2e3fc;
  box-shadow:
    0 1px 2px rgba(60, 64, 67, 0.3),
    0 1px 3px 1px rgba(60, 64, 67, 0.15);
}
.google-signin-btn:active:not(:disabled) {
  background-color: #e8f0fe;
}
.google-signin-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.google-signin-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}
:global(.dark) .google-signin-btn,
.dark .google-signin-btn {
  background-color: #131314;
  color: #e3e3e3;
  border-color: #8e918f;
}
:global(.dark) .google-signin-btn:hover:not(:disabled),
.dark .google-signin-btn:hover:not(:disabled) {
  background-color: rgba(138, 180, 248, 0.08);
  border-color: rgba(138, 180, 248, 0.3);
}
</style>
